import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Key, Desktop, Warning, ShieldCheck, Shield, Check, MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";

const SECTOR_OPTIONS = ["Setor 1", "Setor 2", "Setor 3", "Setor 4"];

/**
 * Página de configurações de LOGINS DE ESTAÇÃO
 * (Administrativo / Desenvolvimento / Produção)
 *
 * O admin controla, por estação:
 *  - Senha do computador físico
 *  - Setor da fábrica onde a máquina está
 *  - Quais páginas do sistema ficam liberadas naquele computador
 *
 * As estações NÃO são pessoas — são "computadores autenticados". Cada ação
 * de colaborador ainda exige o PIN individual (código + senha).
 */
export default function ConfigStationLogins() {
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [permKeys, setPermKeys] = useState([]);

  // Modais
  const [pwEditing, setPwEditing] = useState(null); // {id,name}
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [pwError, setPwError] = useState("");

  const [permEditing, setPermEditing] = useState(null); // {station, perms}
  const [savingPerm, setSavingPerm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: permData }] = await Promise.all([
        api.get("/stations"),
        api.get("/permissions/available"),
      ]);
      setStations(Array.isArray(data) ? data : []);
      setPermKeys(permData?.keys || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar estações");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // ------- Senha
  const openPw = (s) => { setPwEditing(s); setNewPw(""); setConfirmPw(""); setPwError(""); };
  const closePw = () => { setPwEditing(null); setNewPw(""); setConfirmPw(""); setPwError(""); };

  const submitPw = async (e) => {
    e.preventDefault();
    setPwError("");
    if (newPw.length < 3) { setPwError("A senha precisa ter no mínimo 3 caracteres."); return; }
    if (newPw !== confirmPw) { setPwError("As senhas não coincidem."); return; }
    setSavingPw(true);
    try {
      await api.put(`/stations/${pwEditing.id}/password`, { password: newPw });
      toast.success(`Senha da estação "${pwEditing.name}" atualizada`);
      closePw();
      load();
    } catch (err) {
      setPwError(err?.response?.data?.detail || "Erro ao atualizar a senha");
    } finally {
      setSavingPw(false);
    }
  };

  // ------- Setor (inline no card)
  const changeSector = async (station, newSector) => {
    try {
      await api.put(`/stations/${station.id}`, { sector: newSector });
      toast.success(`Setor da estação "${station.name}" atualizado`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao atualizar setor");
    }
  };

  // ------- Permissões
  const openPerms = (s) => {
    const stored = s.permissions || {};
    // Se ainda não configurada, todas ligadas por padrão (mesmo fallback do backend),
    // EXCETO chaves sensíveis que partem desligadas.
    const notConfigured = Object.keys(stored).length === 0;
    const SENSITIVE_OFF = ["admin_actions", "kanban_skip_stages"];
    const merged = {};
    permKeys.forEach((p) => {
      merged[p.key] = notConfigured ? !SENSITIVE_OFF.includes(p.key) : !!stored[p.key];
    });
    setPermEditing({ station: s, perms: merged });
  };
  const closePerms = () => setPermEditing(null);
  const togglePerm = (key) =>
    setPermEditing((prev) => ({ ...prev, perms: { ...prev.perms, [key]: !prev.perms[key] } }));
  const selectAllPerms = (val) => {
    const all = {};
    permKeys.forEach((p) => { all[p.key] = val; });
    setPermEditing((prev) => ({ ...prev, perms: all }));
  };
  const savePerms = async () => {
    setSavingPerm(true);
    try {
      await api.put(`/stations/${permEditing.station.id}`, { permissions: permEditing.perms });
      toast.success(`Permissões da estação "${permEditing.station.name}" atualizadas`);
      closePerms();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao salvar permissões");
    } finally {
      setSavingPerm(false);
    }
  };

  const permsCount = (s) => {
    const stored = s.permissions || {};
    if (Object.keys(stored).length === 0) return `todas (${permKeys.length})`;
    const on = Object.values(stored).filter(Boolean).length;
    return `${on} de ${permKeys.length}`;
  };

  return (
    <div className="p-6 md:p-10" data-testid="config-station-logins">
      <div className="mb-8">
        <div className="overline text-zinc-500 mb-2">CONFIGURAÇÕES · SEGURANÇA</div>
        <h1 className="font-head text-2xl sm:text-3xl font-black tracking-tight">Logins de Estação</h1>
        <p className="text-sm text-zinc-500 mt-3 max-w-2xl">
          As <strong>estações</strong> são os computadores físicos usados na fábrica
          (Administrativo, Desenvolvimento e Produção). Cada máquina se autentica com um
          login único e tem <strong>páginas do sistema liberadas de acordo com o setor</strong>.
          As ações individuais dos colaboradores continuam exigindo PIN a cada movimentação.
        </p>
      </div>

      <div className="border border-yellow-300 bg-yellow-50 p-4 mb-6 flex gap-3" data-testid="stations-warning">
        <Warning size={20} weight="bold" className="shrink-0 mt-0.5" />
        <div className="text-xs text-zinc-700">
          Troque as senhas padrão de fábrica assim que possível. Defina o <strong>setor</strong> e
          restrinja as <strong>permissões</strong> de cada estação para que os computadores mostrem
          apenas os módulos que fazem sentido naquela área.
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-400 py-10 text-center">Carregando…</div>
      ) : stations.length === 0 ? (
        <div className="border border-zinc-200 p-10 text-center text-zinc-400">
          Nenhuma estação cadastrada.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="stations-list">
          {stations.map((s) => (
            <div key={s.id} className="border border-black bg-white p-6 flex flex-col" data-testid={`station-card-${s.name}`}>
              <div className="flex items-center gap-3 mb-4">
                <Desktop size={28} weight="bold" />
                <div>
                  <div className="overline text-zinc-500" style={{ fontSize: 10 }}>ESTAÇÃO</div>
                  <div className="font-head text-xl font-bold">{s.name}</div>
                </div>
              </div>

              <div className="text-xs text-zinc-500 mb-4">
                <div className="flex items-center gap-1 mb-1">
                  <ShieldCheck size={14} weight="bold" />
                  <span className="font-mono">login ativo</span>
                </div>
                <div className="font-mono text-[11px] text-zinc-400">
                  desde {new Date(s.created_at).toLocaleDateString("pt-BR")}
                </div>
              </div>

              {/* Setor */}
              <div className="mb-3">
                <label className="label flex items-center gap-1"><MapPin size={12} weight="bold" /> Setor</label>
                <select
                  className="input text-sm"
                  value={s.sector || ""}
                  onChange={(e) => changeSector(s, e.target.value)}
                  data-testid={`station-sector-${s.name}`}
                >
                  <option value="">— sem setor —</option>
                  {SECTOR_OPTIONS.map((o) => (<option key={o} value={o}>{o}</option>))}
                </select>
              </div>

              {/* Permissões */}
              <div className="mb-4">
                <label className="label flex items-center gap-1"><Shield size={12} weight="bold" /> Páginas liberadas</label>
                <button
                  className="w-full border border-zinc-300 hover:border-black text-sm py-2 text-left px-3 flex items-center justify-between"
                  onClick={() => openPerms(s)}
                  data-testid={`station-perms-${s.name}`}
                >
                  <span className="font-mono text-xs text-zinc-600">{permsCount(s)}</span>
                  <span className="text-xs underline">editar</span>
                </button>
              </div>

              <button
                className="btn w-full text-sm mt-auto"
                onClick={() => openPw(s)}
                data-testid={`station-change-pw-${s.name}`}
              >
                <Key size={14} weight="bold" /> Alterar senha
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal Senha */}
      {pwEditing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={closePw}>
          <div className="glass max-w-md w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="station-pw-modal">
            <div className="overline text-zinc-500 mb-2">ALTERAR SENHA DA ESTAÇÃO</div>
            <h2 className="font-head text-2xl font-bold mb-4">{pwEditing.name}</h2>
            <form onSubmit={submitPw} className="space-y-4">
              <div>
                <label className="label">Nova Senha</label>
                <input type="password" className="input" required autoFocus value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="station-new-pw" />
              </div>
              <div>
                <label className="label">Confirmar Nova Senha</label>
                <input type="password" className="input" required value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="station-confirm-pw" />
              </div>
              {pwError && (
                <div className="border border-[#E60000] bg-red-50 text-[#E60000] text-sm px-3 py-2" data-testid="station-pw-error">
                  {pwError}
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button type="button" className="btn btn-ghost flex-1" onClick={closePw}>Cancelar</button>
                <button type="submit" disabled={savingPw} className="btn flex-1" data-testid="station-pw-submit">
                  {savingPw ? "Salvando..." : "Atualizar senha"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Permissões */}
      {permEditing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={closePerms}>
          <div className="glass max-w-2xl w-full p-8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="station-perm-modal">
            <div className="flex items-center gap-2 mb-2">
              <Shield size={18} weight="bold" />
              <div className="overline text-zinc-500">PÁGINAS LIBERADAS · ESTAÇÃO</div>
            </div>
            <h2 className="font-head text-2xl font-bold mb-1">{permEditing.station.name}</h2>
            <p className="text-xs text-zinc-500 mb-6">
              Marque as páginas que ficarão visíveis quando alguém estiver logado nesse
              computador. Colaboradores continuam autenticando por PIN em cada ação.
            </p>

            <div className="flex items-center justify-between mb-4">
              <span className="text-xs font-mono text-zinc-500">
                {Object.values(permEditing.perms).filter(Boolean).length} de {permKeys.length} marcadas
              </span>
              <div className="flex gap-2">
                <button type="button" onClick={() => selectAllPerms(true)} className="text-xs underline hover:text-black" data-testid="station-perm-all">
                  Marcar todas
                </button>
                <span className="text-zinc-300">|</span>
                <button type="button" onClick={() => selectAllPerms(false)} className="text-xs underline hover:text-black" data-testid="station-perm-clear">
                  Limpar
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
              {permKeys.map((p) => {
                const checked = !!permEditing.perms[p.key];
                return (
                  <label
                    key={p.key}
                    className={`flex items-center gap-3 border p-3 cursor-pointer transition-colors ${
                      checked ? "border-black bg-yellow-50" : "border-zinc-200 hover:border-black"
                    }`}
                    data-testid={`station-perm-row-${p.key}`}
                  >
                    <input
                      type="checkbox"
                      className="w-4 h-4 accent-black"
                      checked={checked}
                      onChange={() => togglePerm(p.key)}
                      data-testid={`station-perm-check-${p.key}`}
                    />
                    <span className="text-sm font-semibold flex-1">{p.label}</span>
                    {checked && <Check size={16} weight="bold" />}
                  </label>
                );
              })}
            </div>

            <div className="flex gap-3">
              <button type="button" className="btn btn-ghost flex-1" onClick={closePerms}>Cancelar</button>
              <button type="button" disabled={savingPerm} onClick={savePerms} className="btn flex-1" data-testid="station-perm-save">
                {savingPerm ? "Salvando..." : "Salvar permissões"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
